/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import $ from 'jquery';
import React, { FunctionComponent } from 'react';
import { change as changeFormValue, reduxForm } from 'redux-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { getAEMResource, getLanguageIso } from '../../selectors/generic';
import { isLoadingById } from '../../selectors/loader';
import { useI18N } from '../my-puratos/hoc/withI18N';
import { isUserLoggedIn, selectUserId } from '../../selectors/auth';
import { OptionSelector } from '../molecules/form/OptionSelector';
import { WithLoading } from '../my-puratos/hoc/withLoading';
import Message from '../my-puratos/molecules/alert/Message';
import { BootstrapVariation } from '../../data/bootstrap/bootstrap.variation';
import { ShowHide } from '../my-puratos/electrons/ShowHide';
import { InputField } from '../molecules/form/InputField';
import {
    ContactCustomerOptions,
    ContactFormComponent,
    ContactReasonIDs,
    ContactTypeIDs,
} from '../../data/definitions/AEMContactForm';
import { TextAreaField } from '../molecules/form/TextAreaField';
import { DropdownField } from '../molecules/form/DropdownField';
import { CheckboxField } from '../molecules/form/CheckboxField';
import RequestButton from '../my-puratos/atoms/button/RequestButton';
import { stateList } from '../../utils/stateList';
import { TypeAheadField } from '../molecules/form/TypeAheadField';
import { selectItemSearchResults } from '../../selectors/search';
import { fetchItemSearch } from '../../actions/search';
import { setFormStatus, submitContactForm } from '../../actions/form';
import {
    fetchDataCallback,
    formalizeGoogleAddressLocation,
    getPlaceDetails,
} from '../../utils/my-puratos/places';
import { GOOGLE_AREA_LEVELS } from '../../utils/googleAreaLevels';
import { ContactFormFields } from '../../utils/my-puratos/FormUtils';
import { useIsMobile } from '../hoc/hooks';
import {
    selectAccountData,
    selectIsAccountDataLoading,
} from '../../selectors/my-puratos/account';
import { GA4ResultType } from '../../analytics/ga4/types/result';
import debounce from '../../utils/debounce';
import { trackGA4ContactForm } from '../../analytics/ga4/events/form';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

export function getSpecificFormValue(options, currentOptionID) {
    let filteredOptionList = options.filter((opt) => opt.id == currentOptionID);
    return filteredOptionList != undefined && filteredOptionList != null
        ? filteredOptionList[0]?.value
        : undefined;
}
interface IProps {
    resource?: ContactFormComponent;
    resourceLoading?: boolean;
    model?: string;
    pristine?: boolean;
    invalid?: boolean;
    handleSubmit?(any);
}

let sessionToken;

export const ContactForm: FunctionComponent<IProps> = ({
    resource,
    resourceLoading,
    model,
    pristine,
    invalid,
    handleSubmit,
}) => {
    const [contactTypeVisible, setContactTypeVisible] = React.useState(true);
    const [contactReasonVisible, setContactReasonVisible] = React.useState(
        false
    );
    const [contactCustomerVisible, setContactCustomerVisible] = React.useState(
        false
    );
    const [contactFormVisible, setContactFormVisible] = React.useState(false);

    const contactTypeRef = React.useRef(null);
    const contactReasonRef = React.useRef(null);
    const contactCustomerRef = React.useRef(null);
    const contactFormRef = React.useRef(null);
    const jobsAnchor = React.useRef(null);
    const formRef = React.useRef(null);
    const messageBannerRef = React.useRef(null);

    const [formValidated, setFormValidated] = React.useState(false);
    const [showAddressFields, setShowAddressFields] = React.useState(true);
    const [showJobsLink, setShowJobsLink] = React.useState<boolean>(false);
    const [addressOptions, setAddressOptions] = React.useState([]);
    const [productFieldValue, setProductFieldValue] = React.useState([]);

    const dispatch = useDispatch();
    const translate = useI18N();
    const isMobile = useIsMobile();
    const userId = useSelector(selectUserId);
    const isLoggedIn = useSelector(isUserLoggedIn);
    const accountDataLoading = useSelector(selectIsAccountDataLoading);
    const accountData = useSelector(selectAccountData);
    const productFieldOptions = useSelector(selectItemSearchResults) || [];
    const contactFormData =
        useSelector((state) => (state as any)?.formData) || {};
    const contactFormValues =
        useSelector((state) => (state as any)?.form?.contact?.values) || {};

    const CORPORATE = 'corporate';

    // AEM resource loaded
    React.useEffect(() => {
        if (resource?.formFields?.enableMapsAPI) {
            setShowAddressFields(false);
            setTimeout(function() {
                if (
                    typeof (window as any).google != 'object' ||
                    typeof (window as any).google.maps != 'object'
                ) {
                    const lang =
                        $('body')
                            ?.attr('data-language')
                            ?.substr(0, 2) || 'en';
                    $.when(
                        $.getScript(
                            'https://maps.googleapis.com/maps/api/js?key=' +
                                resource?.mapsAPIKey +
                                '&libraries=places&sensor=false&language=' +
                                lang
                        )
                    )
                        .done(function(script, textStatus) {})
                        .fail(function(jqxhr, settings, exception) {
                            console.log('Could not load Google Maps API!');
                        });
                }
            }, 500);
        }

        dispatch(
            changeFormValue(
                FORM_NAME,
                ContactFormFields.country,
                resource?.formCountry
            )
        );
    }, [resource?.formFields, resource?.formCountry]);

    // Account data loaded
    React.useEffect(() => initForm(), [accountData]);

    const initForm = () => {
        if (isLoggedIn && !accountDataLoading && accountData) {
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.contactType,
                    accountData?.industry
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.firstName,
                    accountData?.personalInfo?.firstName
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.lastName,
                    accountData?.personalInfo?.lastName
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.companyName,
                    accountData?.companyInfo?.companyName
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.companyVAT,
                    accountData?.companyInfo?.vatNumber
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.email,
                    accountData?.personalInfo?.email
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.customerNumber,
                    accountData?.companyInfo?.customerNumber
                )
            );
            // streetAndNumber contains number/street and street/number divided with ','
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.street,
                    accountData?.companyInfo?.streetAndNumber?.split(',')[0]
                )
            );
            // streetAndNumber contains number/street and street/number divided with ','
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.number,
                    accountData?.companyInfo?.streetAndNumber?.split(', ')[1]
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.box,
                    accountData?.companyInfo?.box
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.postalCode,
                    accountData?.companyInfo?.postalCode
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.city,
                    accountData?.companyInfo?.city
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.state,
                    accountData?.companyInfo?.state
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.addressCountry,
                    accountData?.companyInfo?.country
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.phoneNumber,
                    accountData?.personalInfo?.officePhone ||
                        accountData?.personalInfo?.mobilePhone
                )
            );
            resource?.formCountry &&
                dispatch(
                    changeFormValue(
                        FORM_NAME,
                        ContactFormFields.country,
                        resource?.formCountry
                    )
                );
            setShowAddressFields(true);
            setContactTypeVisible(false);
            setContactReasonVisible(true);
            setContactFormVisible(false);
        }
    };

    // Contact type selection
    React.useEffect(() => {
        if (contactFormValues?.[ContactFormFields.contactType] != undefined) {
            const isContactTypeJob =
                contactFormValues[ContactFormFields.contactType] ==
                ContactTypeIDs.job;
            const isSupplier =
                contactFormValues[ContactFormFields.contactType] ==
                ContactTypeIDs.supplier;
            const proceedToForm = isContactTypeJob || isSupplier;
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.contactReason,
                    undefined
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.contactCustomer,
                    undefined
                )
            );
            setContactReasonVisible(!proceedToForm);
            setContactCustomerVisible(false);
            setContactFormVisible(proceedToForm);
            setShowJobsLink(isContactTypeJob);
        }
    }, [contactFormValues?.[ContactFormFields.contactType]]);

    // Contact reason selection
    React.useEffect(() => {
        if (
            contactFormValues?.[ContactFormFields.contactReason] !== undefined
        ) {
            if (!isLoggedIn) {
                resource?.formFields?.customerSelectMandatory
                    ? setContactCustomerVisible(true)
                    : setContactFormVisible(true);
            } else {
                dispatch(
                    changeFormValue(
                        FORM_NAME,
                        ContactFormFields.contactCustomer,
                        'true'
                    )
                );
            }
        }
    }, [contactFormValues?.[ContactFormFields.contactReason]]);

    // Contact customer / no customer selection
    React.useEffect(() => {
        if (
            contactFormValues?.[ContactFormFields.contactCustomer] !== undefined
        ) {
            setContactFormVisible(true);
        }
    }, [contactFormValues?.[ContactFormFields.contactCustomer]]);

    const getOffset = () => {
        const defaultScrollOffset = document.documentElement.scrollTop - 40;
        const scrollOffset = isMobile
            ? defaultScrollOffset - 64
            : defaultScrollOffset;
        const messageBanner =
            messageBannerRef.current !== null
                ? messageBannerRef?.current?.offsetHeight +
                  36 +
                  (!isMobile && 20)
                : 0;
        return contactTypeVisible
            ? contactTypeRef?.current?.getBoundingClientRect()?.top +
                  scrollOffset -
                  messageBanner
            : contactReasonRef?.current?.getBoundingClientRect()?.top +
                  scrollOffset -
                  messageBanner;
    };

    // ScrollTo refs
    React.useEffect(() => {
        if (contactReasonVisible) {
            contactReasonRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [contactReasonVisible]);
    React.useEffect(() => {
        if (contactCustomerVisible) {
            contactCustomerRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [contactCustomerVisible]);
    React.useEffect(() => {
        // Check if jobs link is visible and scroll to it, before scrolling to contact form
        if (showJobsLink && contactFormVisible) {
            jobsAnchor?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            return;
        }
        if (contactFormVisible) {
            contactFormRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [contactFormVisible]);

    // Maps autocomplete callbacks
    const obtainSessionToken = React.useCallback(() => {
        if (!sessionToken && (window as any).google?.maps?.places) {
            sessionToken = new (window as any).google.maps.places.AutocompleteSessionToken();
        }
        return sessionToken;
    }, [sessionToken]);

    const fetchAddressResults = React.useCallback(
        fetchDataCallback(
            obtainSessionToken(),
            setAddressOptions,
            () => {},
            contactFormValues?.[ContactFormFields.addressCountry] ||
                contactFormValues?.[ContactFormFields.country],
            resource?.mapsAPIKey
        ),
        [
            contactFormValues?.[ContactFormFields.addressCountry],
            contactFormValues?.[ContactFormFields.country],
            obtainSessionToken,
        ]
    );

    const setSelectedAddress = React.useCallback(
        (selected) => {
            Promise.all(
                selected.map((item) =>
                    getPlaceDetails(
                        obtainSessionToken(),
                        item,
                        false,
                        GOOGLE_AREA_LEVELS['Level 1'],
                        formalizeGoogleAddressLocation
                    )
                )
            ).then(
                (data) => {
                    data?.length > 0 && setShowAddressFields(true);
                    sessionToken = null;
                    setAddressFields(data);
                },
                () => {}
            );
        },
        [addressOptions, obtainSessionToken]
    );

    const setAddressFields = (data) => {
        if (data && data[0]) {
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.street,
                    data[0].street
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.number,
                    data[0].number
                )
            );
            dispatch(
                changeFormValue(FORM_NAME, ContactFormFields.box, data[0].box)
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.postalCode,
                    data[0].postalCode
                )
            );
            dispatch(
                changeFormValue(FORM_NAME, ContactFormFields.city, data[0].city)
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.state,
                    data[0].stateLong ||
                    contactFormValues?.[ContactFormFields.state]
                )
            );
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.addressCountry,
                    data[0].country ||
                        contactFormValues?.[ContactFormFields.addressCountry]
                )
            );
        }
    };

    // Fetch product names
    const fetchProductResults = (value) => {
        // Check if country is corporate
        const country =
            resource?.formCountry.toLowerCase() === CORPORATE
                ? CORPORATE
                : countries
                      .find(
                          (country) => country.isocode === resource?.formCountry
                      )
                      .value.toLowerCase();
        const language =
            resource?.formCountry.toLowerCase() === CORPORATE
                ? 'en'
                : getLanguageIso() || 'en';
        dispatch(fetchItemSearch({ term: value, country, language }));
    };

    const debouncedFetchProductResults = debounce(fetchProductResults, 500);

    const handleInputChange = (value) => {
        debouncedFetchProductResults(value);
    };

    // Validate form on submission
    const validateForm = () => {
        if (invalid) {
            setFormValidated(true);
            formRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        } else {
            setFormValidated(false);
        }
    };

    const [recaptchaReady, setRecaptchaReady] = React.useState(false);
    React.useEffect(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
            window.grecaptcha.enterprise.ready(() => setRecaptchaReady(true));
        }
    });

    // Form submission
    const submitHandler = async (formData) => {
        if (recaptchaReady) {
            const recaptchaSiteKey = document.querySelector('head').querySelector(`script[data-recaptcha-sitekey]`)?.getAttribute( 'data-recaptcha-sitekey');
            const recaptchaToken = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action: 'contact_form' });
            formData['g-recaptcha-response'] = recaptchaToken;
        }

        formRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        dispatch(
            submitContactForm({
                path: model,
                data: {
                    ...formData,
                    product:
                        productFieldValue
                            ?.map((product) => product.title)
                            .join(', ') || '',
                    productId:
                        productFieldValue
                            ?.map((product) => product.productCode)
                            .join(';') || '',
                    puratosFormUserAddress:
                        contactFormValues?.[ContactFormFields.email] || '',
                    puratosFormCountry:
                        contactFormValues?.[ContactFormFields.country] || '',
                    puratosContactEntityLabel:
                        resource?.contactProfiles?.filter(
                            (profile) =>
                                profile?.id ===
                                contactFormValues?.[
                                    ContactFormFields.contactType
                                ]
                        )[0]?.value || '',
                    puratosContactReasonLabel:
                        resource?.contactReasons?.filter(
                            (reason) =>
                                reason?.id ===
                                contactFormValues?.[
                                    ContactFormFields.contactReason
                                ]
                        )[0]?.value || '',
                },
            })
        );
    };

    const resetFormStatus = () => {
        dispatch(setFormStatus(undefined));
    };

    const resetAddressFields = () => {
        dispatch(changeFormValue(FORM_NAME, ContactFormFields.address, ''));
        setAddressFields([
            {
                street: '',
                number: '',
                box: '',
                postalCode: '',
                city: '',
                stateLong: '',
            },
        ]);
        setShowAddressFields(false);
    };

    const initSelectCountry = () => {
        if (resource?.formCountry.toLowerCase() !== CORPORATE) {
            dispatch(
                changeFormValue(
                    FORM_NAME,
                    ContactFormFields.addressCountry,
                    resource?.formCountry
                )
            );
        }
    };

    React.useEffect(() => {
        if (contactFormData?.status) {
            if (isLoggedIn) {
                initForm();
            } else {
                setContactTypeVisible(true);
                setContactReasonVisible(false);
                setContactCustomerVisible(false);
                setContactFormVisible(false);
                setShowAddressFields(!resource?.formFields?.enableMapsAPI);
                dispatch(
                    changeFormValue(
                        FORM_NAME,
                        ContactFormFields.country,
                        resource?.formCountry
                    )
                );
            }

            // Datalayer push for analytics
            trackGA4ContactForm(
                GA4ResultType.SUCCESS,
                userId,
                contactFormData?.email
            );

            const event = {
                event: 'ContactForm',
                eventCategory: 'Contact Form',
                eventAction: 'Submit',
                eventLabel: 'Contact page',
                eventValue: window.location.href,
            };
            (window as any)?.dataLayer?.push(event);
        }
    }, [contactFormData?.status]);

    const fields = resource?.formFields;
    const messages = resource?.formMessages;
    const errorMsg = messages?.fieldErrorMsg;
    const states = stateList.filter((item) =>
        [
            contactFormValues?.[
                ContactFormFields.addressCountry
            ]?.toUpperCase(),
        ].includes(item.iso)
    );
    const countries = resource?.availableCountryList;
    const isMultipleCountries = countries?.length > 1;
    const isCustomerNumberEmpty =
        !isLoggedIn &&
        contactFormValues?.[ContactFormFields.customerNumber]?.length > 0;
    const showProductField =
        contactFormValues?.[ContactFormFields.contactReason] ==
            ContactReasonIDs.productInfo ||
        contactFormValues?.[ContactFormFields.contactReason] ==
            ContactReasonIDs.support;
    const showCustomerNumberField =
        isLoggedIn ||
        contactFormValues?.[ContactFormFields.contactCustomer] === 'true';
    const isNotJobApContactType =
        contactFormValues?.[ContactFormFields.contactType] !=
        ContactTypeIDs.job;
    const isNotSupplierContactType =
        contactFormValues?.[ContactFormFields.contactType] !=
        ContactTypeIDs.supplier;

    return (
        <React.Fragment>
            <WithLoading
                loading={resourceLoading || (isLoggedIn && accountDataLoading)}
            >
                <form onSubmit={handleSubmit(submitHandler)} ref={formRef}>
                    <Message
                        forwardRef={messageBannerRef}
                        message={resource?.formSuccessMessage}
                        show={contactFormData?.status || false}
                        type={BootstrapVariation.success}
                        lifetime={12000}
                        marginTop
                        marginBottom
                        autoClose
                        onClose={resetFormStatus}
                    />
                    <Message
                        message={resource?.formErrorMessage}
                        show={contactFormData?.status === false}
                        type={BootstrapVariation.danger}
                        onClose={resetFormStatus}
                        marginTop
                        marginBottom
                    />
                    <Message
                        message={resource?.formIncompleteMessage}
                        show={formValidated}
                        marginTop
                        marginBottom
                        type={BootstrapVariation.danger}
                        onClose={() => setFormValidated(false)}
                    />
                    <ShowHide show={contactTypeVisible}>
                        <div ref={contactTypeRef}>
                            <OptionSelector
                                title={'Who are you?'}
                                name={ContactFormFields.contactType}
                                options={resource?.contactProfiles}
                            />
                        </div>
                    </ShowHide>
                    <ShowHide show={showJobsLink && contactFormVisible}>
                        <a
                            href={'https://jobs.puratos.com'}
                            target={'_blank'}
                            rel="noreferrer"
                            ref={jobsAnchor}
                        >
                            <h6>
                                {translate(
                                    'Please visit our Jobs page to join the Puratos team'
                                )}
                            </h6>
                        </a>
                    </ShowHide>
                    <ShowHide show={contactReasonVisible}>
                        <div ref={contactReasonRef}>
                            <OptionSelector
                                title={'Choose contact reason'}
                                name={ContactFormFields.contactReason}
                                options={resource?.contactReasons}
                                currentContactProfile={getSpecificFormValue(
                                    resource?.contactProfiles,
                                    contactFormValues?.puratosFormToAddress
                                )}
                            />
                        </div>
                    </ShowHide>
                    <ShowHide show={contactCustomerVisible}>
                        <div ref={contactCustomerRef}>
                            <OptionSelector
                                title={'Customer'}
                                name={ContactFormFields.contactCustomer}
                                options={ContactCustomerOptions}
                            />
                        </div>
                    </ShowHide>
                    <ShowHide show={contactFormVisible}>
                        <h3 ref={contactFormRef}>
                            {translate('How can we help?')}
                        </h3>

                        <InputField
                            name={ContactFormFields.firstName}
                            label={'First name'}
                            maxLength={40}
                            isRequired
                            infoMsg={messages?.firstNameMsg}
                            errorMsg={errorMsg}
                            hasError={formValidated}
                        />
                        <InputField
                            name={ContactFormFields.lastName}
                            label={'Last name'}
                            maxLength={40}
                            isRequired
                            infoMsg={messages?.lastNameMsg}
                            errorMsg={errorMsg}
                            hasError={formValidated}
                        />
                        <ShowHide show={isNotJobApContactType}>
                            <InputField
                                name={ContactFormFields.companyName}
                                label={'Company name'}
                                maxLength={40}
                                isRequired
                                infoMsg={messages?.companyNameMsg}
                                errorMsg={errorMsg}
                                hasError={formValidated}
                            />
                        </ShowHide>
                        <ShowHide
                            show={
                                isNotJobApContactType &&
                                isNotSupplierContactType
                            }
                        >
                            <InputField
                                name={ContactFormFields.companyVAT}
                                label={'Company VAT'}
                                maxLength={40}
                                infoMsg={messages?.companyVatMsg}
                                errorMsg={errorMsg}
                                hasError={formValidated}
                            />
                        </ShowHide>
                        <InputField
                            name={ContactFormFields.email}
                            label={'E-mail'}
                            type={'email'}
                            maxLength={256}
                            isRequired
                            infoMsg={messages?.emailMsg}
                            errorMsg={errorMsg}
                            hasError={formValidated}
                        />
                        {showCustomerNumberField &&
                            fields?.customerNumberOptional && (
                                <InputField
                                    name={ContactFormFields.customerNumber}
                                    label={'Customer number'}
                                    maxLength={60}
                                    infoMsg={messages?.customerNumberMsg}
                                    errorMsg={errorMsg}
                                    hasError={formValidated}
                                />
                            )}

                        <ShowHide
                            show={
                                !isCustomerNumberEmpty &&
                                !showAddressFields &&
                                fields?.enableMapsAPI &&
                                isNotJobApContactType
                            }
                        >
                            <TypeAheadField
                                name={ContactFormFields.address}
                                label={'Address'}
                                options={addressOptions}
                                resultKey={'description'}
                                onInputChange={fetchAddressResults}
                                setSelectedInput={setSelectedAddress}
                                infoMsg={messages?.addressMsg}
                                errorMsg={messages?.fieldErrorMsg}
                                isRequired={true}
                                emptyLabel={translate(
                                    'Type to search an address'
                                )}
                                hasError={formValidated}
                                insertedOption={
                                    contactFormValues?.[
                                        ContactFormFields.address
                                    ]
                                }
                                selectedOption={
                                    contactFormValues?.[
                                        ContactFormFields.street
                                    ]
                                }
                                className={'contactFormAddressFieldJS'}
                            />

                            <span className={"p-link-small p-link--brand-secondary"} onClick={() => setShowAddressFields(true)}>
                                    {translate("Or enter address manually")}
                            </span>
                        </ShowHide>

                        <ShowHide
                            show={
                                !isCustomerNumberEmpty &&
                                (showAddressFields || !fields?.enableMapsAPI) &&
                                isNotJobApContactType
                            }
                        >
                            {fields?.enableMapsAPI && (
                                <div className="row">
                                    <div className="col-md-6 col-sm-12 form--input__reset">
                                        <span onClick={resetAddressFields}>
                                            Reset
                                        </span>
                                    </div>
                                </div>
                            )}
                            {
                                <ShowHide show={!fields?.hideStreet}>
                                    <InputField
                                        name={ContactFormFields.street}
                                        label={'Street'}
                                        maxLength={60}
                                        isRequired
                                        errorMsg={errorMsg}
                                        hasError={formValidated}
                                    />
                                </ShowHide>
                            }
                            {
                                <ShowHide show={!fields?.hideNumber}>
                                    <InputField
                                        name={ContactFormFields.number}
                                        label={'Number'}
                                        maxLength={10}
                                        isRequired
                                        errorMsg={errorMsg}
                                        hasError={formValidated}
                                    />
                                </ShowHide>
                            }
                            {(fields?.boxMandatory || fields?.boxOptional) && (
                                <InputField
                                    name={ContactFormFields.box}
                                    label={'Box'}
                                    maxLength={10}
                                    isRequired={fields?.boxMandatory}
                                    errorMsg={errorMsg}
                                    hasError={formValidated}
                                />
                            )}
                            {
                                <ShowHide show={!fields?.hideZip}>
                                    <InputField
                                        name={ContactFormFields.postalCode}
                                        label={'Postal code'}
                                        maxLength={10}
                                        isRequired
                                        errorMsg={errorMsg}
                                        hasError={formValidated}
                                    />
                                </ShowHide>
                            }
                            {
                                <ShowHide show={!fields?.hideCity}>
                                    <InputField
                                        name={ContactFormFields.city}
                                        label={'City'}
                                        maxLength={40}
                                        isRequired
                                        errorMsg={errorMsg}
                                        hasError={formValidated}
                                    />
                                </ShowHide>
                            }
                            {fields?.stateMandatory && states?.length > 0 && (
                                <DropdownField
                                    name={ContactFormFields.state}
                                    label={'State'}
                                    maxLength={6}
                                    list={states}
                                    isRequired
                                    errorMsg={errorMsg}
                                    hasError={formValidated}
                                    hasHiddenField
                                />
                            )}
                        </ShowHide>

                        {!isCustomerNumberEmpty && (
                            <DropdownField
                                name={ContactFormFields.addressCountry}
                                label={'Country'}
                                optionId={'isocode'}
                                optionValue={'title'}
                                maxLength={6}
                                list={countries}
                                isRequired
                                errorMsg={errorMsg}
                                hasError={formValidated}
                                disabled={!isMultipleCountries}
                                handlerInitializer={initSelectCountry}
                                hasHiddenField
                            />
                        )}
                        {!isCustomerNumberEmpty && (
                            <InputField
                                name={ContactFormFields.phoneNumber}
                                label={'Phone number'}
                                maxLength={40}
                                isRequired
                                infoMsg={messages?.phoneMsg}
                                errorMsg={errorMsg}
                                hasError={formValidated}
                            />
                        )}
                        {showProductField && (
                            <TypeAheadField
                                name={ContactFormFields.product}
                                label={'Product'}
                                options={productFieldOptions}
                                infoMsg={messages?.productMsg}
                                errorMsg={errorMsg}
                                onInputChange={handleInputChange}
                                setSelectedInput={setProductFieldValue}
                                resultKey={'title'}
                                maxLength={256}
                                emptyLabel={translate(
                                    'Type to search a product'
                                )}
                                hasError={formValidated}
                                insertedOption={
                                    contactFormValues?.[
                                        ContactFormFields.product
                                    ]
                                }
                                selectedOption={productFieldValue}
                                multiple
                                maxResults={10}
                            />
                        )}

                        <TextAreaField
                            name={ContactFormFields.message}
                            label={'Message'}
                            maxLength={256}
                            isRequired
                            infoMsg={messages?.messageMsg}
                            errorMsg={errorMsg}
                            hasError={formValidated}
                        />

                        {fields?.optinLabel && (
                            <CheckboxField
                                name={ContactFormFields.optIn}
                                label={fields?.optinLabel}
                                hasError={formValidated}
                            />
                        )}

                        {
                            <CheckboxField
                                name={ContactFormFields.termsAndConditions}
                                label={fields?.privacyPolicyLabel}
                                hasError={formValidated}
                                isRequired
                            />
                        }

                        <div className={'row'}>
                            <div
                                className={
                                    'col-md-6 col-sm-12 form--input__submit-btn'
                                }
                            >
                                <RequestButton
                                    label={translate(fields?.sendButtonText)}
                                    loading={contactFormData?.submitting}
                                    disabled={
                                        pristine || contactFormData?.submitting
                                    }
                                    onClick={validateForm}
                                />
                            </div>
                        </div>
                    </ShowHide>
                </form>
            </WithLoading>
        </React.Fragment>
    );
};

const FORM_NAME = 'contact';

const mapActionsToProps = {};

export default reduxForm({ form: FORM_NAME })(
    connect(
        (state, ownProps: any) => ({
            resource: getAEMResource(state, ownProps.model) || {},
            resourceLoading: isLoadingById(state, ownProps.model),
        }),
        mapActionsToProps
    )(ContactForm)
);
